import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import {
  LANGUAGE_STORAGE_KEY,
  getTranslation,
  interpolate,
  normalizeLanguage,
  normalizeLookupKey
} from "./translations"

const LanguageContext = createContext(null)

const STATUS_KEYS = {
  active: "status.active",
  pending: "status.pending",
  deactivated: "status.deactivated",
  blocked: "status.blocked",
  rejected: "status.rejected",
  accepted: "status.accepted",
  enroute: "status.enroute",
  arrived: "status.arrived",
  completed: "status.completed",
  cancelled: "status.cancelled",
  canceled: "status.cancelled",
  disputed: "status.disputed"
}

const SERVICE_KEYS = {
  economy: "service.economy",
  family: "service.family",
  medical: "service.medical",
  luxury: "service.luxury",
  schooltrips: "service.schooltrips",
  schooltrip: "service.schooltrips",
  scooter: "service.scooter"
}

const VEHICLE_KEYS = {
  car: "vehicle.car",
  sedan: "vehicle.sedan",
  suv: "vehicle.suv",
  van: "vehicle.van",
  scooter: "vehicle.scooter",
  motorcycle: "vehicle.motorcycle",
  motorbike: "vehicle.motorbike"
}

const GOVERNORATE_KEYS = {
  cairo: "governorate.cairo",
  giza: "governorate.giza",
  asyut: "governorate.asyut",
  assiut: "governorate.asyut",
  redsea: "governorate.redsea",
  southsinai: "governorate.southsinai",
  alexandria: "governorate.alexandria"
}

const API_ERROR_KEYS = {
  "login failed. please check your credentials.": "auth.loginFailed",
  "invalid email or password": "auth.loginFailed",
  "invalid credentials": "auth.loginFailed",
  "connection error. please try again.": "auth.connectionError",
  "failed to fetch users": "errors.fetchUsers",
  "failed to update user status": "errors.updateUserStatus",
  "failed to update governorate": "errors.updateGovernorate",
  "failed to fetch captains": "errors.fetchCaptains",
  "failed to fetch bookings": "errors.fetchBookings",
  "failed to fetch financial data": "errors.fetchFinancials",
  "admin session expired. please log in again.": "errors.sessionExpired"
}

function getInitialLanguage() {
  if (typeof window === "undefined") return "en"
  try {
    const saved = window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
    if (saved) return normalizeLanguage(saved)
  } catch (error) {
    // Local storage can be unavailable in hardened browser modes.
  }
  return normalizeLanguage(window.navigator?.language)
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(getInitialLanguage)
  const isRTL = language === "ar"

  useEffect(() => {
    document.documentElement.lang = language
    document.documentElement.dir = isRTL ? "rtl" : "ltr"
    document.body.dataset.language = language
    document.title = isRTL ? "Tout's Routes | لوحة تحكم الإدارة" : "Tout's Routes | Admin Dashboard"
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
    } catch (error) {
      // The interface remains usable even when persistence is unavailable.
    }
  }, [isRTL, language])

  const setLanguage = useCallback((value) => setLanguageState(normalizeLanguage(value)), [])
  const toggleLanguage = useCallback(
    () => setLanguageState((current) => (current === "ar" ? "en" : "ar")),
    []
  )

  const t = useCallback(
    (key, params = {}) => {
      const localized = getTranslation(language, key)
      const fallback = getTranslation("en", key)
      return interpolate(localized ?? fallback ?? key, params)
    },
    [language]
  )

  const translateLookup = useCallback(
    (value, mapping) => {
      const key = mapping[normalizeLookupKey(value)]
      return key ? t(key) : value || t("common.notAvailable")
    },
    [t]
  )

  const translateStatus = useCallback(
    (value) => translateLookup(value, STATUS_KEYS),
    [translateLookup]
  )
  const translateService = useCallback(
    (value) => translateLookup(value, SERVICE_KEYS),
    [translateLookup]
  )
  const translateVehicle = useCallback(
    (value) => translateLookup(value, VEHICLE_KEYS),
    [translateLookup]
  )
  const translateGovernorate = useCallback(
    (value) => translateLookup(value, GOVERNORATE_KEYS),
    [translateLookup]
  )
  const translateApiMessage = useCallback(
    (value, fallbackKey = "errors.generic") => {
      if (!value) return t(fallbackKey)
      const key = API_ERROR_KEYS[String(value).trim().toLowerCase()]
      return key ? t(key) : language === "ar" ? t(fallbackKey) : value
    },
    [language, t]
  )

  const locale = language === "ar" ? "ar-EG" : "en-GB"
  const formatNumber = useCallback(
    (value, options = {}) => new Intl.NumberFormat(locale, options).format(Number(value || 0)),
    [locale]
  )
  const formatCurrency = useCallback(
    (value) =>
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "EGP",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Number(value || 0)),
    [locale]
  )
  const formatDateTime = useCallback(
    (value) => new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)),
    [locale]
  )

  const value = useMemo(
    () => ({
      language,
      isRTL,
      setLanguage,
      toggleLanguage,
      t,
      translateStatus,
      translateService,
      translateVehicle,
      translateGovernorate,
      translateApiMessage,
      formatNumber,
      formatCurrency,
      formatDateTime
    }),
    [
      formatCurrency,
      formatDateTime,
      formatNumber,
      isRTL,
      language,
      setLanguage,
      t,
      toggleLanguage,
      translateApiMessage,
      translateGovernorate,
      translateService,
      translateVehicle,
      translateStatus
    ]
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) throw new Error("useLanguage must be used inside LanguageProvider")
  return context
}
